import { useTranslations } from 'next-intl'

import { ContractPopover } from '@/entities/contract'
import { useFormatCurrency } from '@/shared/lib'
import { Icon, Separator, ContentBox, Description } from '@/shared/ui'

import { UpdateDepositReturnAccountButton } from '../deposit-return-account-popup/show-dialog-button'
import { UpdateDepositReturnCardButton } from '../deposit-return-card-popup/show-dialog-button'

export type Props = {
  usingFee: number
  longTermDiscount: number
  earlyDiscount: number
  mgmtFee: number
  cleanFee: number
  contractFee: number
  realUsingFee: number
  deposit: number
  finalPayFee: number
  isCancelled: boolean
  isExtend: boolean
  hasDepositReturnAccountInfo: boolean
  isDepositReturnAccountButtonVisible: boolean
  isDepositReturnCardButtonVisible: boolean
}

export const GuestPayInformationDetail = ({
  usingFee,
  longTermDiscount,
  earlyDiscount,
  mgmtFee,
  cleanFee,
  contractFee,
  realUsingFee,
  deposit,
  finalPayFee,
  isCancelled,
  isExtend,
  hasDepositReturnAccountInfo,
  isDepositReturnAccountButtonVisible,
  isDepositReturnCardButtonVisible,
}: Props) => {
  const t = useTranslations()

  const { formatCurrency } = useFormatCurrency()

  const showDepositReturnAccountTooltip = !hasDepositReturnAccountInfo

  const showLongTermDiscount = longTermDiscount > 0
  const showEarlyDiscount = earlyDiscount > 0

  return (
    <div className="space-y-3">
      <Description.List>
        <Description.Item
          className="justify-between"
          label={t('common.rent')}
          value={formatCurrency(usingFee)}
          lineThrough={isCancelled}
        />

        {showLongTermDiscount && (
          <Description.Item className="justify-between">
            <Description.ItemLabel highlight>
              {t('common.discount.long_term')}
            </Description.ItemLabel>
            <Description.ItemValue highlight lineThrough={isCancelled}>
              {formatCurrency(longTermDiscount, { negative: true })}
            </Description.ItemValue>
          </Description.Item>
        )}

        {showEarlyDiscount && (
          <Description.Item className="justify-between">
            <Description.ItemLabel highlight>
              {t('common.discount.immediate')}
            </Description.ItemLabel>
            <Description.ItemValue highlight lineThrough={isCancelled}>
              {formatCurrency(earlyDiscount, { negative: true })}
            </Description.ItemValue>
          </Description.Item>
        )}

        <Description.Item
          className="justify-between"
          label={t('common.utility_fee')}
          value={formatCurrency(mgmtFee)}
          lineThrough={isCancelled}
        />

        <Description.Item className="items-start justify-between">
          <Description.ItemLabel>
            <span>{t('common.cleaning_fee')}</span>
            {isExtend && (
              <span className="typo-label2 block font-normal">
                {t(
                  'lease_request.payment_summary_item_cleaning_fee_description',
                )}
              </span>
            )}
          </Description.ItemLabel>
          <Description.ItemValue lineThrough={isCancelled}>
            {formatCurrency(cleanFee)}
          </Description.ItemValue>
        </Description.Item>

        <Description.Item
          className="justify-between"
          label={t('common.service_fee')}
          value={formatCurrency(contractFee)}
          lineThrough={isCancelled}
        />
      </Description.List>

      <Separator />

      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel semiBold className="text-black">
            {t('common.actual_cost')}
          </Description.ItemLabel>
          <Description.ItemValue
            bold
            highlight
            className="typo-headline3"
            lineThrough={isCancelled}
          >
            {formatCurrency(realUsingFee)}
          </Description.ItemValue>
        </Description.Item>

        <Description.Item
          className="justify-between"
          label={t('common.deposit')}
          value={formatCurrency(deposit)}
          lineThrough={isCancelled}
        />

        <ContentBox
          color="purple"
          className="px-3"
          align={
            isDepositReturnAccountButtonVisible ||
            isDepositReturnCardButtonVisible
              ? 'between'
              : 'center'
          }
        >
          {(() => {
            if (isExtend) {
              return t(
                'lease_request.payment_summary_lease_extension_deposit_description',
              )
            }

            return t('common.deposit_notice')
          })()}

          {isDepositReturnAccountButtonVisible && (
            <ContractPopover
              defaultOpen={showDepositReturnAccountTooltip}
              message={t('lease_detail.tooltip.deposit_information_required')}
            >
              <UpdateDepositReturnAccountButton color="purple">
                {(() => {
                  if (hasDepositReturnAccountInfo) {
                    return t('lease_detail.button.check_deposit_information')
                  }

                  return t('lease_detail.button.deposit_information')
                })()}
                <Icon name="arrow-right" />
              </UpdateDepositReturnAccountButton>
            </ContractPopover>
          )}

          {isDepositReturnCardButtonVisible && (
            <UpdateDepositReturnCardButton color="purple">
              {t('lease_detail.button.check_deposit_information')}
              <Icon name="arrow-right" />
            </UpdateDepositReturnCardButton>
          )}
        </ContentBox>
      </Description.List>

      <Separator color="black" />

      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel bold className="text-black">
            {t('common.total_payment')}
          </Description.ItemLabel>
          <Description.ItemValue bold className="typo-headline3">
            {formatCurrency(finalPayFee)}
          </Description.ItemValue>
        </Description.Item>
      </Description.List>
    </div>
  )
}
